import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import type { MeResponse, SessionCode, SessionUser } from '@inventohub/shared';
import { ApiClientError, apiRequest } from './api';

interface AuthState {
  user: SessionUser | null;
  code: SessionCode | null;
  loading: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [code, setCode] = useState<SessionCode | null>(null);
  const [loading, setLoading] = useState(true);

  const applyMe = useCallback((body: MeResponse) => {
    setUser(body.user ?? null);
    setCode(body.code ?? null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const body = await apiRequest<MeResponse>('/api/auth/me');
      applyMe(body);
    } catch (error: unknown) {
      if (error instanceof ApiClientError && error.httpStatus === 401) {
        setUser(null);
        setCode(null);
        return;
      }
      throw error;
    }
  }, [applyMe]);

  const logout = useCallback(async () => {
    try {
      await apiRequest('/api/auth/logout', { method: 'POST' });
    } catch (error: unknown) {
      if (!(error instanceof ApiClientError && error.httpStatus === 401)) throw error;
    } finally {
      setUser(null);
      setCode(null);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const body = await apiRequest<MeResponse>('/api/auth/me');
        if (!cancelled) applyMe(body);
      } catch {
        if (!cancelled) {
          setUser(null);
          setCode(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyMe]);

  const value = useMemo(
    () => ({ user, code, loading, refresh, logout }),
    [user, code, loading, refresh, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth requires AuthProvider');
  return value;
}

export function RequireAuth({ children }: { children: ReactNode }): JSX.Element {
  const { user, code, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <p className="meta" data-testid="auth-loading">
        Checking sign-in…
      </p>
    );
  }

  if (!user && !code) {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }

  return <>{children}</>;
}
